import React, { useEffect, useState, useCallback } from "react";
import { useSwipeable } from "react-swipeable";
import api from "../../api/axios";
import { useTranslation } from "react-i18next";
import AppModalShell, {
  AppModalButton,
  AppModalContentItem,
} from "../common/AppModalShell";

/**
 * StreakModal
 *
 * ✅ Pure‑UI version — wired to mock data only.
 *    (Back‑end integration will be done later.)
 *
 * Props
 * -----
 * visible   : boolean  – show / hide the modal
 * onClose   : () => void
 *
 * TODO (next steps)
 *  • wire to real streak API
 *  • fire `checkin` request on mount when needed
 *  • connect “Collect” button
 */

const StreakModal = ({ visible = false, onClose }) => {
  const { t } = useTranslation('menu');
  const [allRewards, setAllRewards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    if (!visible) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const checkinRes = await api.get("/miniapp/checkin-streak/");
        setCurrentStreak(checkinRes.data.current_streak);
        setExpired(!!checkinRes.data.expired);
        const current = checkinRes.data.current_streak;
        if (!checkinRes.data.reward_type && current <= 0 && !checkinRes.data.expired) {
          onClose?.();
          return;
        }
        const rewardsRes = await api.get("/miniapp/streak-rewards/", { params: { center: current, window: 10 } });
        // backend returns array of reward objects in rewardsRes.data.rewards
        const rewardsArr = Array.isArray(rewardsRes.data?.rewards) ? rewardsRes.data.rewards : [];
        // exclude non-positive days
        const validRewards = rewardsArr.filter(
          (item) => item.streak_day > 0
            && item.reward_type === "coins"
            && Number(item.reward_value || 0) > 0
        );
        if (validRewards.length === 0) {
          onClose?.();
          return;
        }
        setAllRewards(validRewards);
        // find index by is_today flag (fallback to 0)
        const idx = validRewards.findIndex(item => item.is_today);
        setCurrentIndex(idx >= 0 ? idx : 0);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [visible, onClose]);

  /* ----- swipe left / right ------------------------------------------------ */
  const shift = useCallback(
    (dir) => {
      setCurrentIndex((idx) => {
        const next = idx + dir;
        if (next < 0 || next >= allRewards.length) return idx;
        return next;
      });
    },
    [allRewards.length]
  );

  const handlers = useSwipeable({
    onSwipedLeft: () => shift(1),
    onSwipedRight: () => shift(-1),
    trackMouse: true,
    preventScrollOnSwipe: true,
    delta: 18,
    touchEventOptions: { passive: false },
  });

  /* ------------------------------------------------------------------------ */
  if (!visible) return null;

  // Map raw backend array; use the backend fields directly
  const mappedRewards = allRewards.map((item) => ({
    day: item.streak_day,
      rewardValue: item.reward_value,
      rewardValueFallback: item.reward_amount,
      claimed: item.claimed,
      is_today: item.is_today,
      description: item.description,
  }));

  const centre = mappedRewards[currentIndex];
  const left = mappedRewards[currentIndex - 1];
  const right = mappedRewards[currentIndex + 1];

  // Disabled if reward already claimed, or day is in the future
  const centreRewardValue = centre?.rewardValue ?? centre?.rewardValueFallback ?? 0;
  const isDisabled = loading || claiming || expired || !centre || centre.claimed || centre.day > currentStreak;

  // Collect handler: call backend and update reward state
  const handleCollect = async () => {
    if (isDisabled) return;
    setClaiming(true);
    try {
      await api.post("/miniapp/streak-claim/", { day: centre.day });
      // Mark the current reward as claimed
      setAllRewards((prev) => {
        const updated = [...prev];
        if (updated[currentIndex]) {
          updated[currentIndex] = {
            ...updated[currentIndex],
            claimed: true,
          };
        }
        return updated;
      });
    } catch (err) {
      console.error("Streak claim failed:", err);
    } finally {
      setClaiming(false);
    }
  };

  return (
    <AppModalShell
      open={visible}
      title={expired ? t("streak.header.title_lost") : t("streak.header.title_great")}
      description={expired ? t("streak.header.subtitle_lost") : t("streak.header.subtitle_great")}
      onClose={onClose}
      closeAriaLabel={t("streak.header.close_aria")}
      zIndexClassName="z-[13020]"
      maxWidthClassName="max-w-[390px]"
      titleClassName="text-[22px] sm:text-[28px]"
      descriptionClassName="mb-1 text-center text-[17px] font-semibold leading-6"
      bodyClassName="overflow-hidden px-5 pb-5 pt-2"
      contentClassName="overflow-hidden"
      footer={
        <AppModalButton
          onClick={expired ? onClose : handleCollect}
          disabled={expired ? false : isDisabled}
          className="text-[#2563EB]"
        >
          {expired
            ? t("streak.cta.ok")
            : centre?.claimed
              ? t("streak.cta.claimed")
              : claiming
                ? t("streak.cta.collecting")
                : t("streak.cta.collect")}
        </AppModalButton>
      }
    >
      <AppModalContentItem
        className="flex flex-col"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        style={{ touchAction: 'pan-y' }}
        {...handlers}
      >
        <div className="flex items-center justify-center gap-8">
          {left ? (
            <button
              type="button"
              onClick={() => shift(-1)}
              className="flex w-14 touch-manipulation flex-col items-center"
              aria-label={t("streak.timeline.prev_day_aria", { day: left.day })}
            >
              <div className={`flex h-10 w-10 items-center justify-center rounded-full ${left.day <= currentStreak ? 'bg-white' : 'bg-white/50'}`}>
                {left.claimed && (
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 8L6.5 11.5L13 5" stroke="#0073FF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <span className={`mt-2 text-xs font-extrabold ${left.claimed ? 'text-white' : 'text-white/50'}`}>
                {t("streak.timeline.day_label", { day: left.day })}
              </span>
            </button>
          ) : (
            <div className="w-14" />
          )}

          <div className="flex w-36 flex-col items-center">
            <div className={`flex h-28 w-28 items-center justify-center rounded-full ${
              expired
                ? 'bg-red-600'
                : centre && centre.claimed
                ? 'bg-white'
                : centre && centre.day <= currentStreak
                ? 'bg-white'
                : 'bg-white/50'
            }`}
            >
              {centre && centre.claimed && (
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 25L22 35L36 16" stroke="#0073FF" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
          </div>

          {right ? (
            <button
              type="button"
              onClick={() => shift(1)}
              className="flex w-14 touch-manipulation flex-col items-center"
              aria-label={t("streak.timeline.next_day_aria", { day: right.day })}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/50">
                {right.claimed && (
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 8L6.5 11.5L13 5" stroke="#0073FF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <span className="mt-2 text-xs font-extrabold text-white/50">
                {t("streak.timeline.day_label", { day: right.day })}
              </span>
            </button>
          ) : (
            <div className="w-14" />
          )}
        </div>

        <div className="mt-8 text-center text-white">
          <p className="text-3xl font-extrabold">
            {centre ? t("streak.centre.day_label", { day: centre.day }) : ""}
          </p>
          <p className="mt-3 flex items-center justify-center text-2xl font-medium">
            {centre ? t("streak.centre.reward_prefix", { value: centreRewardValue }) : ""}
            <img
              src="/assets/vault/coin_icon.svg"
              alt={t("streak.centre.coin_alt")}
              className="ml-1 inline-block h-5 w-5"
            />
          </p>
        </div>
      </AppModalContentItem>
    </AppModalShell>
  );
};

export default StreakModal;
